import { PagingData } from "@lib/titan2/dist/game/control/data/PagingData";
import { Paging } from "@lib/titan2/dist/game/control/Paging";
import { Swipe } from "@lib/titan2/dist/game/control/Swipe";
import { TitanButton } from "@lib/titan2/dist/game/control/TitanButton";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { FactoryManager } from "@lib/titan2/dist/game/factory/FactoryManager";
import { AbstractLoader } from "@lib/titan2/dist/game/view/display/AbstractLoader";
import { LoaderEvent } from "@lib/titan2/dist/loader/event/LoaderEvent";
import { DeviceUtil } from "@lib/titan2/dist/util/device/DeviceUtil";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { ObjectUtil } from "@lib/titan2/dist/util/object/ObjectUtil";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { GraphicsUtil } from "@lib/titan2/dist/util/view/GraphicsUtil";
import { gsap } from "gsap";
import * as PIXI from "pixi.js";
/**
 * LoaderDialog.ts
 */
export class LoaderDialog extends AbstractLoader {
    constructor(properties, portraitProperties, version) {
        super(properties, portraitProperties, version);
    }
    run() {
        super.run();
        if (this.running) {
            this.startPageTimer();
        }
    }
    sleep() {
        if (this.running) {
            this.pageTimer.kill();
        }
        super.sleep();
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.background);
        this.background = null;
        DestroyUtil.destroy(this.progressBar);
        this.progressBar = null;
        DestroyUtil.destroy(this.progressBarMask);
        this.progressBarMask = null;
        DestroyUtil.destroy(this.progressField);
        this.progressField = null;
        this.landscapeProperties = null;
        this.portraitProperties = null;
        this.uiProperties = null;
        DestroyUtil.destroy(this.playButton);
        this.playButton = null;
        DestroyUtil.destroy(this.progressBarBackground);
        this.progressBarBackground = null;
        DestroyUtil.destroy(this.logo);
        this.logo = null;
        DestroyUtil.destroy(this.paging);
        this.paging = null;
        DestroyUtil.destroy(this.pageHolder);
        this.pageHolder = null;
        this.pages.forEach(page => DestroyUtil.destroy(page));
        this.pages = null;
        this.currentPage = null;
        DestroyUtil.destroy(this.previousButton);
        this.previousButton = null;
        DestroyUtil.destroy(this.nextButton);
        this.nextButton = null;
        DestroyUtil.destroy(this.swipe);
        this.swipe = null;
        DestroyUtil.destroy(this.previousIndex);
        this.previousIndex = null;
        DestroyUtil.destroy(this.data);
        this.data = null;
        DestroyUtil.destroy(this.completeHandler);
        this.completeHandler = null;
        this.pageTimer.kill();
        this.pageTimer = null;
    }
    create() {
        this.createModel();
        Logger.addObjectRef(this, "LoaderDialog");
        this.createBackground();
        this.createProgressBar();
        this.createProgressBarMask();
        this.createVersionField();
        this.createProgressField();
        this.createLogo();
        this.createPageHolder();
        this.createPreviousButton();
        this.createNextButton();
        this.createPaging();
        this.createPages();
        this.createSwipe();
        this.createPlayButton();
        this.updatePage();
        this.loadStartTime = new Date().getTime();
    }
    postCreate() {
        super.postCreate();
        Logger.addObjectRef(this, "LoaderDialog");
    }
    createModel() {
        this.model = FactoryManager.getInstance()?.getFactory()?.getModel();
    }
    createProgressBar() {
        if (this.uiProperties.progressBarBackgroundProperties) {
            this.progressBarBackground = new TitanSprite(this.landscapeProperties.progressBarBackgroundProperties, this.portraitProperties?.progressBarBackgroundProperties);
            this.progressBarBackground.name = "progressBarBackground";
            this.addChild(this.progressBarBackground);
        }
        this.progressBar = new TitanSprite(this.landscapeProperties.progressBarProperties, this.portraitProperties?.progressBarProperties);
        this.progressBar.name = "progressBar";
        this.originalWidth = this.progressBar.width;
        this.addChild(this.progressBar);
    }
    createProgressBarMask() {
        let size = new PIXI.Point(this.progressBar.width, this.progressBar.height);
        this.progressBarMask = GraphicsUtil.createMask(size);
        this.progressBarMask.position.copyFrom(this.uiProperties.progressBarProperties.position);
        this.progressBar.mask = this.progressBarMask;
        this.progressBarMask.width = 1;
        this.addChild(this.progressBarMask);
    }
    createProgressField() {
        this.progressField = TitanText.createField(this, this.landscapeProperties.progressFieldProperties, this.portraitProperties?.progressFieldProperties);
    }
    createLogo() {
        this.logo = new TitanSprite(this.landscapeProperties.logoProperties, this.portraitProperties?.logoProperties);
        this.logo.name = "logo";
        this.addChild(this.logo);
    }
    createPageHolder() {
        this.pageHolder = new PIXI.Container();
        this.pageHolder.name = "pageHolder";
        this.addChild(this.pageHolder);
    }
    createPreviousButton() {
        this.previousButton = new TitanButton(this.landscapeProperties.previousButtonProperties, this.portraitProperties?.previousButtonProperties);
        this.previousButton.name = "previousButton";
        this.previousButton.onClick(this.handlePreviousButtonSelected, this);
        this.addChild(this.previousButton);
    }
    createNextButton() {
        this.nextButton = new TitanButton(this.landscapeProperties.nextButtonProperties, this.portraitProperties?.nextButtonProperties);
        this.nextButton.onClick(this.handleNextButtonSelected, this);
        this.nextButton.name = "nextButton";
        this.addChild(this.nextButton);
    }
    createPaging() {
        this.paging = new Paging(this.landscapeProperties.pagingProperties, this.portraitProperties?.pagingProperties);
        this.paging.name = "paging";
        this.paging.onChange(this.handlePagingUpdated, this);
        this.paging.update(new PagingData(this.uiProperties.getTotalPages(), 1));
        this.addChild(this.paging);
    }
    createPages() {
        this.pages = [];
        let pagePropertiesL = this.landscapeProperties.pageProperties;
        let pagePropertiesP = this.portraitProperties?.pageProperties;
        if (pagePropertiesP && (pagePropertiesL.length !== pagePropertiesP.length)) {
            throw new Error("Page property length mismatch!");
        }
        for (let i = 0; i < pagePropertiesL.length; i++) {
            this.pages.push(this.createPage(pagePropertiesL[i], pagePropertiesP[i]));
        }
    }
    createSwipe() {
        if (DeviceUtil.isTouch()) {
            this.swipe = new Swipe(this.landscapeProperties.swipeProperties, this.portraitProperties?.swipeProperties);
            this.swipe.onSwipeLeft(this.handleSwipeLeft, this);
            this.swipe.onSwipeRight(this.handleSwipeRight, this);
            this.addChild(this.swipe);
        }
    }
    createPlayButton() {
        this.playButton = new TitanButton(this.landscapeProperties.playButtonProperties, this.portraitProperties?.playButtonProperties);
        this.playButton.name = "playButton";
        this.playButton.onClick(this.handleClose, this);
        this.addChild(this.playButton);
        this.playButton.setEnabled(false);
        this.playButton.renderable = false;
    }
    handleClose() {
        if (this.data) {
            this.data.dispose();
        }
        this.dispatchCore(LoaderEvent.LOADER_REMOVE);
    }
    handleLoadingComplete() {
        this.updateProgress(100);
        this.callAfter(1000, this.completeLoadingProgress);
        const endTime = new Date().getTime();
        this.model.dispatchKPI("TITAN_STATE", "GAME_LOADING_PAGE_COMPLETED", (endTime - this.loadStartTime).toString());
    }
    completeLoadingProgress() {
        this.dispatchCore(LoaderEvent.PROGRESS_COMPLETE);
        this.playButton.setEnabled(true);
        this.playButton.renderable = true;
        this.progressBar.renderable = false;
        this.progressBarBackground.renderable = false;
        this.progressField.renderable = false;
    }
    handleNextButtonSelected() {
        this.next();
    }
    handlePreviousButtonSelected() {
        this.previous();
    }
    handleSwipeLeft() {
        if (this.previousButton.isEnabled()) {
            this.previous();
        }
    }
    handleSwipeRight() {
        if (this.nextButton.isEnabled()) {
            this.next();
        }
    }
    handlePageTimer() {
        this.paging.next();
        this.startPageTimer();
    }
    next() {
        this.pageTimer.kill();
        this.paging.next();
        this.startPageTimer();
    }
    previous() {
        this.paging.previous();
    }
    handlePagingUpdated() {
        this.updatePaging();
    }
    update(data) {
        this.data = data;
        this.previousIndex = 0;
        this.updatePaging();
    }
    updatePaging() {
        this.removePage();
        this.updatePage();
        this.updateButtons();
        this.previousIndex = this.paging.getIndex();
    }
    updatePage() {
        let pageIndex = this.paging.getIndex();
        let page = this.pages[pageIndex];
        page.x = 0;
        this.pageHolder.addChild(page);
        this.currentPage = page;
    }
    updateButtons() {
        let data = this.paging.getData();
        let pageIndex = data.getPageIndex();
        this.nextButton.setEnabled(pageIndex + 1 < data.getTotalCount());
        this.previousButton.setEnabled(pageIndex - 1 >= 0);
    }
    updateProgress(value) {
        let width = Math.round(this.originalWidth * (value / 100));
        width = width > this.originalWidth ? this.originalWidth : width;
        gsap.to(this.progressBarMask, {
            width: width,
            duration: 1,
            onUpdate: () => {
                this.progressField.setText(String(Math.round(this.progressBarMask.width / this.originalWidth * 100)) + "%");
            }
        });
    }
    startPageTimer() {
        this.pageTimer = gsap.delayedCall(this.uiProperties.pageDurationMs / 1000, this.handlePageTimer.bind(this));
    }
    createPage(properties, portraitProperties) {
        return ObjectUtil.create(properties.PageClass, properties, portraitProperties);
    }
    removePage() {
        if (this.currentPage) {
            this.pageHolder.removeChild(this.currentPage);
            this.currentPage = null;
        }
    }
    autoPosition() {
        if (this.renderer.isLandscape()) {
            this.scaleLandscape();
        }
        else {
            this.scalePortrait();
        }
        let rendererSize = this.renderer.getGameSize();
        this.position.set(rendererSize.x / 2, rendererSize.y / 2);
        this.pivot.set(rendererSize.x / 2, rendererSize.y / 2);
    }
    handleResize() {
        this.autoPosition();
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
    }
    scaleLandscape() {
        let newScale = new PIXI.Point(1, 1);
        let x = (this.renderer.getWindowSize().x / this.renderer.getHolder().scale.x);
        let viewportWidth = this.renderer.getViewport().width;
        let minScale = this.renderer.getMinimumScale();
        if (x < viewportWidth) {
            let scale = Math.max(minScale, x / viewportWidth);
            scale = parseFloat(scale.toFixed(2));
            newScale = new PIXI.Point(scale, scale);
        }
        this.scale.copyFrom(newScale);
    }
    scalePortrait() {
        let newScale = new PIXI.Point(1, 1);
        let y = (this.renderer.getWindowSize().y / this.renderer.getHolder().scale.y);
        let viewportHeight = this.renderer.getViewport().height;
        let minScale = 0.6;
        if (y < viewportHeight) {
            let scale = Math.max(minScale, y / viewportHeight);
            scale = parseFloat(scale.toFixed(2));
            newScale = new PIXI.Point(scale, scale);
        }
        this.scale.copyFrom(newScale);
    }
}
//# sourceMappingURL=LoaderDialog.js.map